import { useSortable } from '@dnd-kit/sortable';
import { Button } from 'components/atoms';
import RadialAudioPlayer from 'components/atoms/audio-player/RadialAudioPlayer';
import { FormInput } from 'components/molecules';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { PiDotsSixVertical, PiNavigationArrow, PiTrash } from 'react-icons/pi';
import { useGetTrackLightList } from 'services/tracks/light';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import './TrackRoleCard.scss';

export interface ITrackForm {
  id: string;
  name: string;
  mp3PreviewLink: string;
}

interface ITrackRoleCardProps {
  track: ITrackForm;
  index: number;
  displayMode?: boolean;
  onRemove?: () => void;
  name?: string;
  sortableKey: string;
}

const TrackRoleCard = ({
  track,
  index,
  displayMode,
  onRemove,
  name = 'tracks',
  sortableKey,
}: ITrackRoleCardProps) => {
  const { values, setFieldValue }: IFormikContextType<Record<string, ITrackForm[]>> =
    useFormikContext<Record<string, ITrackForm[]>>();
  const { t } = useTranslation();

  const tracksLight = useGetTrackLightList();

  const allSelectedTrackIds: string[] = Object.values(values)
    .flatMap((v: any) =>
      Array.isArray(v)
        ? v.flatMap((album: any) =>
            Array.isArray(album.tracks) ? album.tracks.map((t: ITrackForm) => t.id) : [],
          )
        : [],
    )
    .filter(Boolean);

  // odfiltruj już wybrane (poza bieżącym trackiem)
  const tracksSelectOptions =
    tracksLight.data
      ?.filter((t) => !allSelectedTrackIds.includes(t.id) || t.id === track.id)
      .map((t) => ({
        label: t.mainTitle || 'unknown title',
        value: t.id,
      })) ?? [];

  const draggingDisabled = !track.name || !track.id;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableKey,
    disabled: draggingDisabled,
  });

  const style: CSSProperties = {
    transform: transform ? `translateY(${transform.y}px)` : 'none',
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div className="track-role-card" style={style}>
      {!displayMode && (
        <div className="draggable" ref={setNodeRef} {...attributes} {...listeners}>
          <Button isClear onlyIcon disabled={draggingDisabled}>
            <Button.Icon icon={<PiDotsSixVertical />} />
          </Button>
        </div>
      )}
      {displayMode && track.id && (
        <RadialAudioPlayer playerId={track.id} audioSrc={track.mp3PreviewLink ?? ''} />
      )}
      {displayMode ? (
        <div className="track-role-name">
          <div className="track-role-header">{track.name}</div>
        </div>
      ) : (
        <div className="track-role-inputs">
          <FormInput.Select
            name={`${name}[${index}].id`}
            placeholder={t('input.label.track')}
            label={t('input.label.track')}
            appBackground
            onChange={(value) => {
              setFieldValue(`${name}.id`, value.value);
              setFieldValue(`${name}.name`, value.label);
            }}
            value={{
              label: track.name,
              value: track.id,
            }}
            options={tracksSelectOptions}
            isLoading={tracksLight.isLoading}
            isAsync
            isSearchable
          />
        </div>
      )}

      {displayMode ? (
        <Button
          className="flip-icon-x"
          isClear
          onlyIcon
          onClick={() => window.open(`/track/${track.id}/data`, '_blank')}
        >
          <Button.Icon icon={<PiNavigationArrow />} />
        </Button>
      ) : (
        <Button isClear onlyIcon variant={BUTTON_VARIANT.remove} onClick={onRemove}>
          <Button.Icon icon={<PiTrash />} />
        </Button>
      )}
    </div>
  );
};

export default TrackRoleCard;
